import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { AlertTriangle, Shield, FileText, Calendar, BarChart3, Plus, Send, CheckCircle, Clock, TrendingUp, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

/* ── Incidents ── */
type IncidentSeverity = "low" | "medium" | "high" | "critical";
type IncidentStatus = "open" | "overdue" | "under_review" | "closed";

interface Incident {
  id: string; ref: string; vessel: string; date: string; type: string; severity: IncidentSeverity;
  status: IncidentStatus; investigator: string; closeOutDue: string; description: string;
  rootCause?: string; correctiveActions?: string[];
}

const INCIDENTS: Incident[] = [
  { id: "1", ref: "VOFF-INC-2026-001", vessel: "VOFF Crewliner 01", date: "2026-02-14", type: "Near Miss", severity: "low", status: "closed", investigator: "E. Ercegovic", closeOutDue: "2026-02-21", description: "Unsecured tool dropped from upper deck during crew transfer. No injuries. Tool landed on passenger walkway.", rootCause: "Procedure Non-Compliance", correctiveActions: ["Toolbox talk on deck safety", "Secure storage audit completed"] },
  { id: "2", ref: "VOFF-INC-2026-002", vessel: "VOFF Combi 01", date: "2026-03-08", type: "First Aid", severity: "low", status: "closed", investigator: "E. Ercegovic", closeOutDue: "2026-03-15", description: "Minor hand laceration during mooring operations. Treated on vessel.", rootCause: "Equipment gap (worn gloves)", correctiveActions: ["PPE inspection schedule updated", "New glove stock ordered"] },
  { id: "3", ref: "VOFF-INC-2026-003", vessel: "VOFF Crewliner 01", date: "2026-03-22", type: "Near Miss", severity: "medium", status: "open", investigator: "E. Ercegovic", closeOutDue: "2026-03-29", description: "Crew member slipped on wet deck. Caught handrail — no fall. Non-slip coating worn in high-traffic area.", rootCause: "Environmental + Maintenance", correctiveActions: ["Non-slip coating renewal scheduled", "Wet deck warning signs installed"] },
  { id: "4", ref: "VOFF-INC-2026-004", vessel: "VOFF Combi 01", date: "2026-04-05", type: "Property Damage", severity: "medium", status: "open", investigator: "E. Ercegovic", closeOutDue: "2026-04-12", description: "Fender contact with FPSO accommodation ladder during berthing in 1.5m swell. Minor fender damage. No structural impact." },
];

/* ── Bulletins ── */
const BULLETINS = [
  { ref: "VOFF-SB-2026-001", title: "Gangway Safety — Lessons from Recent CTV Incidents", date: "2026-03-05", source: "IMCA", vesselsSent: "All", status: "issued" },
  { ref: "VOFF-SB-2026-002", title: "DP Watchkeeping — IMCA Alert M 220", date: "2026-03-18", source: "IMCA", vesselsSent: "All", status: "issued" },
  { ref: "VOFF-SB-2026-003", title: "Fatigue Management — Hours of Rest Compliance", date: "2026-04-02", source: "Internal", vesselsSent: "All", status: "issued" },
];

/* ── Drills ── */
interface Drill { type: string; frequency: string; lastCompleted: string; nextDue: string; status: "completed" | "due" | "overdue" }
const DRILLS: Drill[] = [
  { type: "Abandon Ship / Muster Drill", frequency: "Weekly", lastCompleted: "2026-04-12", nextDue: "2026-04-19", status: "completed" },
  { type: "Fire Drill", frequency: "Monthly", lastCompleted: "2026-04-01", nextDue: "2026-05-01", status: "completed" },
  { type: "Man Overboard (MOB) Drill", frequency: "Monthly", lastCompleted: "2026-03-15", nextDue: "2026-04-15", status: "due" },
  { type: "Enclosed Space Entry Drill", frequency: "2-Monthly", lastCompleted: "2026-03-01", nextDue: "2026-05-01", status: "completed" },
  { type: "Oil Spill Response Drill", frequency: "Quarterly", lastCompleted: "2025-12-15", nextDue: "2026-03-15", status: "overdue" },
  { type: "DP Failure Mode Drill", frequency: "Monthly", lastCompleted: "2026-04-05", nextDue: "2026-05-05", status: "completed" },
  { type: "Crew Medical Emergency Drill", frequency: "Quarterly", lastCompleted: "2026-02-20", nextDue: "2026-05-20", status: "completed" },
  { type: "Security / ISPS Drill", frequency: "Quarterly", lastCompleted: "2026-01-15", nextDue: "2026-04-15", status: "due" },
  { type: "Cyber Security Drill", frequency: "6-Monthly", lastCompleted: "2026-01-01", nextDue: "2026-07-01", status: "completed" },
];

const sevBadge = (s: IncidentSeverity) => {
  const m: Record<IncidentSeverity, string> = { low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", medium: "bg-amber-500/15 text-amber-400 border-amber-500/30", high: "bg-orange-500/15 text-orange-400 border-orange-500/30", critical: "bg-red-500/15 text-red-400 border-red-500/30" };
  return <Badge variant="outline" className={`text-[10px] font-bold uppercase ${m[s]}`}>{s}</Badge>;
};

const statusBadge = (s: IncidentStatus) => {
  const m: Record<IncidentStatus, { label: string; cls: string }> = {
    open: { label: "OPEN", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
    overdue: { label: "OVERDUE", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
    under_review: { label: "UNDER REVIEW", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
    closed: { label: "CLOSED", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  };
  return <Badge variant="outline" className={`text-[10px] font-bold ${m[s].cls}`}>{m[s].label}</Badge>;
};

const drillBadge = (s: "completed" | "due" | "overdue") => {
  const m = { completed: "🟢", due: "🟡 DUE", overdue: "🔴 OVERDUE" };
  return <span className="text-xs font-body">{m[s]}</span>;
};

export const HSESuiteContent = () => {
  const [tab, setTab] = useState("incidents");
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showBulletinModal, setShowBulletinModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  const ltiFreeDays = 247;
  const completedDrills = DRILLS.filter(d => d.status === "completed").length;
  const drillCompliance = Math.round((completedDrills / DRILLS.length) * 100);

  return (
    <>
      {/* Header */}
      <div className="bg-[#00283C] rounded-xl p-6 mb-6 border-l-4 border-red-500/50">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-[#8CB43C] text-xl font-bold tracking-wider uppercase font-display">HSE Suite</h1>
            <p className="text-white/60 text-sm font-body mt-1">Incident Management | Safety Bulletins | Drill Tracker</p>
            <p className="text-white/40 text-[10px] font-body mt-1">OVMSA2 Elements 6 & 7 — Incident Reporting & Emergency Response</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-8 py-4 text-center">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-body">LTI-Free Days</p>
            <p className="text-white text-4xl font-bold font-display">{ltiFreeDays}</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="font-body mb-6">
          <TabsTrigger value="incidents"><AlertTriangle className="w-4 h-4 mr-1.5" /> Incident Register</TabsTrigger>
          <TabsTrigger value="bulletins"><FileText className="w-4 h-4 mr-1.5" /> Safety Bulletins</TabsTrigger>
          <TabsTrigger value="drills"><Calendar className="w-4 h-4 mr-1.5" /> Drill Tracker</TabsTrigger>
          <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-1.5" /> HSE Dashboard</TabsTrigger>
        </TabsList>

        {/* Incidents */}
        <TabsContent value="incidents">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-foreground font-body">Incident Register</h3>
            <Button size="sm" onClick={() => setShowIncidentModal(true)} className="bg-red-600 hover:bg-red-700 font-body text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Submit Incident / Near Miss
            </Button>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Ref</th>
                    <th className="text-left px-4 py-3">Vessel</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Severity</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Investigator</th>
                    <th className="text-left px-4 py-3">Close-Out Due</th>
                  </tr>
                </thead>
                <tbody>
                  {INCIDENTS.map(inc => (
                    <tr key={inc.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => setSelectedIncident(inc)}>
                      <td className="px-4 py-3 font-mono text-xs text-[#288CC8]">{inc.ref}</td>
                      <td className="px-4 py-3 text-foreground">{inc.vessel}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{inc.date}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inc.type}</td>
                      <td className="px-4 py-3">{sevBadge(inc.severity)}</td>
                      <td className="px-4 py-3">{statusBadge(inc.status)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inc.investigator}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{inc.closeOutDue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Bulletins */}
        <TabsContent value="bulletins">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-foreground font-body">Safety Bulletin Library</h3>
            <Button size="sm" onClick={() => setShowBulletinModal(true)} className="bg-[#288CC8] hover:bg-[#288CC8]/80 font-body text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Generate Bulletin
            </Button>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Ref</th>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-left px-4 py-3">Vessels</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {BULLETINS.map((b, i) => (
                    <tr key={i} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-xs text-[#288CC8]">{b.ref}</td>
                      <td className="px-4 py-3 text-foreground font-medium">{b.title}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{b.date}</td>
                      <td className="px-4 py-3 text-muted-foreground">{b.source}</td>
                      <td className="px-4 py-3 text-muted-foreground">{b.vesselsSent}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border-emerald-500/30">ISSUED</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Drills */}
        <TabsContent value="drills">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-foreground font-body">Drill Schedule — SOLAS / ISM Requirements</h3>
            <div className="flex items-center gap-3">
              <Select defaultValue="all">
                <SelectTrigger className="w-[180px] font-body text-sm"><SelectValue placeholder="All Vessels" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vessels</SelectItem>
                  <SelectItem value="crewliner01">VOFF Crewliner 01</SelectItem>
                  <SelectItem value="combi01">VOFF Combi 01</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => toast.success("Action recorded: Log Drill")} size="sm" className="bg-[#8CB43C] hover:bg-[#8CB43C]/80 font-body text-xs">
                <Plus className="w-3.5 h-3.5 mr-1" /> Log Drill
              </Button>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Drill Type</th>
                    <th className="text-left px-4 py-3">Frequency</th>
                    <th className="text-left px-4 py-3">Last Completed</th>
                    <th className="text-left px-4 py-3">Next Due</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {DRILLS.map((d, i) => (
                    <tr key={i} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 text-foreground font-medium">{d.type}</td>
                      <td className="px-4 py-3 text-muted-foreground">{d.frequency}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{d.lastCompleted}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{d.nextDue}</td>
                      <td className="px-4 py-3">{drillBadge(d.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-xs text-red-400 font-body">Oil Spill Response Drill is overdue by 32 days — schedule immediately</p>
          </div>
        </TabsContent>

        {/* Dashboard */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            {[
              { label: "LTI-Free Days", value: ltiFreeDays.toString(), color: "text-emerald-400", status: "🟢" },
              { label: "TRIR (12-month)", value: "0.00", color: "text-emerald-400", status: "Target: ≤ 1.0" },
              { label: "Near Misses YTD", value: "4", color: "text-amber-400", status: "Target: ≥ 12" },
              { label: "Open Incidents", value: INCIDENTS.filter(i => i.status === "open").length.toString(), color: "text-amber-400", status: "" },
              { label: "Drill Compliance", value: `${drillCompliance}%`, color: drillCompliance === 100 ? "text-emerald-400" : "text-amber-400", status: "Target: 100%" },
              { label: "Bulletins YTD", value: BULLETINS.length.toString(), color: "text-[#288CC8]", status: "" },
            ].map(kpi => (
              <div key={kpi.label} className="bg-card border border-border rounded-xl p-4 text-center">
                <p className={`text-2xl font-bold font-display ${kpi.color}`}>{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-body mt-1">{kpi.label}</p>
                {kpi.status && <p className="text-[9px] text-muted-foreground font-body mt-0.5">{kpi.status}</p>}
              </div>
            ))}
          </div>
          <Button onClick={() => toast.success("Monthly HSE Report generated")} className="bg-[#00283C] hover:bg-[#00283C]/80 font-body text-sm">
            <FileText className="w-4 h-4 mr-2" /> Generate Monthly HSE Report
          </Button>
        </TabsContent>
      </Tabs>

      {/* Incident Submission Modal */}
      <Dialog open={showIncidentModal} onOpenChange={setShowIncidentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Submit Incident / Near Miss</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Select Vessel" /></SelectTrigger>
              <SelectContent><SelectItem value="cl01">VOFF Crewliner 01</SelectItem><SelectItem value="co01">VOFF Combi 01</SelectItem></SelectContent></Select>
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" className="font-body text-sm" />
              <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>{["Bridge", "Engine Room", "Deck", "Gangway", "Galley", "Other"].map(l => <SelectItem key={l} value={l.toLowerCase()}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Incident Type" /></SelectTrigger>
              <SelectContent>{["Near Miss", "First Aid", "Medical Treatment Case", "Lost Time Injury", "Property Damage", "Environmental"].map(t => <SelectItem key={t} value={t.toLowerCase().replace(/ /g, "_")}>{t}</SelectItem>)}</SelectContent></Select>
            <Textarea placeholder="Describe the incident (minimum 10 words)..." className="font-body text-sm min-h-[100px]" />
            <Input placeholder="Reported by" className="font-body text-sm" />
            <Button onClick={() => { setShowIncidentModal(false); toast.success("Incident submitted — AI classification in progress..."); setTimeout(() => toast.info("AI Classification: LOW severity — Near Miss. Investigator: E. Ercegovic. Deadline: 7 days."), 2000); }} className="w-full bg-red-600 hover:bg-red-700 font-body">Submit Report</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulletin Generator Modal */}
      <Dialog open={showBulletinModal} onOpenChange={setShowBulletinModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Generate Safety Bulletin</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Source Material" /></SelectTrigger>
              <SelectContent>{["IMCA Safety Alert", "MAIB Investigation Report", "Flag State Notice", "VOFF Internal Incident", "Manual Input"].map(s => <SelectItem key={s} value={s.toLowerCase().replace(/ /g, "_")}>{s}</SelectItem>)}</SelectContent></Select>
            <Textarea placeholder="Paste source text or alert reference..." className="font-body text-sm min-h-[100px]" />
            <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Tone" /></SelectTrigger>
              <SelectContent><SelectItem value="informational">Informational</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="reminder">Reminder</SelectItem></SelectContent></Select>
            <Button onClick={() => { setShowBulletinModal(false); toast.success("Bulletin generated — review and issue"); }} className="w-full bg-[#288CC8] hover:bg-[#288CC8]/80 font-body">Generate with AI</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Incident Detail Modal */}
      <Dialog open={!!selectedIncident} onOpenChange={() => setSelectedIncident(null)}>
        <DialogContent className="max-w-2xl">
          {selectedIncident && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  <span className="text-[#288CC8] font-mono text-sm">{selectedIncident.ref}</span>
                  {selectedIncident.type}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-2">{sevBadge(selectedIncident.severity)} {statusBadge(selectedIncident.status)}</div>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm font-body">
                  <div><span className="text-muted-foreground">Vessel:</span> <span className="font-medium ml-2">{selectedIncident.vessel}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium ml-2">{selectedIncident.date}</span></div>
                  <div><span className="text-muted-foreground">Investigator:</span> <span className="font-medium ml-2">{selectedIncident.investigator}</span></div>
                  <div><span className="text-muted-foreground">Close-Out Due:</span> <span className="font-medium ml-2">{selectedIncident.closeOutDue}</span></div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-1">Description</h4>
                  <p className="text-sm font-body text-foreground">{selectedIncident.description}</p>
                </div>
                {selectedIncident.rootCause && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-1">Root Cause</h4>
                    <p className="text-sm font-body text-foreground">{selectedIncident.rootCause}</p>
                  </div>
                )}
                {selectedIncident.correctiveActions && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-1">Corrective Actions</h4>
                    {selectedIncident.correctiveActions.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm font-body py-1">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                        <span>{a}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const HSESuite = () => (
  <IntranetLayout>
    <HSESuiteContent />
  </IntranetLayout>
);

export default HSESuite;
